import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { MultiSelect } from 'primereact/multiselect'
import { classNames } from 'primereact/utils'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Loader from 'Components/Loader'
import DialogModal from 'Components/UIComponents/DialogModal'
import { ButtonComponent } from 'Components/UIComponents'

const UserDashboard = ({ isLoading, setLoading, setChangesMade, mockUserDashboard, mockCurrentSelection }) => {
  const { t } = useTranslation()

  const [adminUserList, setAdminUserList] = useState([])
  const [selectedUsers, setSelectedUsers] = useState(null)
  const [currentSelection, setCurrentSelection] = useState([])
  const [permittedUsers, setPermittedUsers] = useState([])
  const [availableId, setAvailableId] = useState()
  const [editSettings, setEditSettings] = useState({
    editMode: false,
    submitButton: false,
    workbookEditIcon: true,
    toggle: false,
    editText: false
  })
  const [deleteIconEnabled, setDeleteIconEnabled] = useState(false)
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })

  useEffect(() => {
    getPermittedUsers()

    // testing purpose
    if (mockUserDashboard) {
      setCurrentSelection(mockCurrentSelection)
    }
  }, [])

  const getPermittedUsers = async () => {
    setLoading(true)
    try {
      const [appSettingsResponse, adminUsersResponse] = await Promise.all([apiAdapterCoffeeWeb.appSetting(), apiAdapterCoffeeWeb.getAllOtherUsers()])

      const availableUserIds = appSettingsResponse?.data?.isDashboardAvailable?.split(',').map((item) => Number(item))
      const adminUsersData = adminUsersResponse?.data

      const filteredUserList = adminUsersData?.filter((el) => availableUserIds.includes(el.id))

      setAvailableId(availableUserIds)
      setAdminUserList(adminUsersData)
      setSelectedUsers(filteredUserList)
      setPermittedUsers(filteredUserList)
      setCurrentSelection(filteredUserList)
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_DATA_FOUND'),
        onRetry: () => getPermittedUsers(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUserDataSubmit = async () => {
    setLoading(true)
    try {
      const updatedUserIds = availableId?.length ? availableId.join(',') : 0
      const response = await apiAdapterCoffeeWeb.updateDashboardAvailable(updatedUserIds)

      if (response.data.statusCode === 200) {
        setChangesMade(false)
        setEditSettings({
          editMode: false,
          submitButton: false
        })

        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('DASHBOARD_USER_DATA_UPDATED'),
          onHide: handleDismiss,
          onSuccess: handleDismiss
        })
        setDeleteIconEnabled(false)
      }
    } catch {
      setSelectedUsers(permittedUsers)
      setCurrentSelection(permittedUsers)
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('DASHBOARD_DATA_UPDATE_FAIL'),
        onRetry: () => handleUserDataSubmit(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEditButtonClick = () => {
    setEditSettings((prevEditSettings) => ({
      ...prevEditSettings,
      editMode: !prevEditSettings.editMode,
      submitButton: true
    }))

    setDeleteIconEnabled(true)
  }

  const handleReset = () => {
    const userIds = permittedUsers.map((user) => user.id)

    setSelectedUsers(permittedUsers)
    setCurrentSelection(permittedUsers)
    setAvailableId([...userIds])
    setChangesMade(false)
    setEditSettings((prev) => ({ ...prev, editMode: false, submitButton: false }))
  }

  const handleSelectedUsers = (event) => {
    setSelectedUsers([...event.value])
    setCurrentSelection([...event.value])
    setChangesMade(true)

    const userIds = [...event.value].map((user) => user.id)

    setAvailableId([...userIds])
  }

  const handleDismiss = () => {
    setShowDialog({
      ...showDialog,
      showModal: false
    })
  }

  const handleDelete = (item) => {
    const updatedUserList = currentSelection.filter((el) => el?.id !== item?.id)
    const userIds = updatedUserList.map((user) => user.id)

    setChangesMade(true)
    setAvailableId([...userIds])
    setCurrentSelection(updatedUserList)
    setSelectedUsers(updatedUserList)
  }

  return (
    <div className="user-dashboard-data" data-testid="user-dashboard-data">
      <div className="user-dashboard-input-container">
        {editSettings.editMode && <MultiSelect value={selectedUsers} name="country-multi-select" onChange={(e) => handleSelectedUsers(e)} options={adminUserList} optionLabel="email" filter placeholder={t('SELECT_USER')} maxSelectedLabels={1} data-testid="user-dropdown" />}

        <div className={!editSettings.editMode ? 'edit' : ''}>
          {!editSettings.editMode && (
            <div className="user-dashboard-edit-container">
              <ButtonComponent type={'edit'} onClick={() => handleEditButtonClick()} />
            </div>
          )}
        </div>
      </div>

      <div className={`user-dashboard-email ${!currentSelection?.length ? 'no-user-data' : ''}`}>
        {currentSelection?.length > 0 ? (
          <>
            {currentSelection.map((ele, index) => (
              <div key={index} className="dashboard-item-container" data-testid="dashboard-item-container">
                <div className="dashboard-item" data-testid="dashboard-item">
                  {ele.email}
                </div>
                {currentSelection && (
                  <div className="user-dashboard-delete-button">
                    <Button
                      icon="pi pi-trash delete-icon"
                      tooltip={t('DELETE')}
                      onClick={() => handleDelete(ele)}
                      tooltipOptions={{ position: 'top' }}
                      className={classNames('p-button-rounded p-button-text', {
                        'p-button-danger': editSettings.editMode,
                        'p-button-secondary': !editSettings.editMode
                      })}
                      disabled={!deleteIconEnabled}
                      data-testid="delete-button"
                    />
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          <>
            {!isLoading && (
              <div className="dashboard-access">
                <div>{t('CHOOSE_USERS_TO_GRANT_ACCESS_TO_THE_DASHBOARD')}</div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="user-dashboard-button-container">
        {editSettings.submitButton && (
          <div className="user-dashboard-buttons">
            <ButtonComponent type={'cancel'} onClick={() => handleReset()} />
            <ButtonComponent type={'submit'} onClick={() => handleUserDataSubmit()} />
          </div>
        )}
      </div>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </div>
  )
}

export default Loader(UserDashboard)
